import type { ReactElement } from 'react';
import React from 'react';
import { broadcastQueryClient } from '@tanstack/query-broadcast-client-experimental';
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';

const CACHE_TIME = CACHE_LONG;
const STALE_TIME = STALE_SHORT;

/**
 * ASSIST:API - query client
 *
 * Create a query (data fetching) client & set defaults
 */
export const AssistQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,

      // Turns retries off, so that queries that return undefined by design are not automatically re-run:
      retry: false
    }
  }
});

/* Only set the following up if we are not in a test environment */
if (SITE_ENV !== 'test') {
  /**
   * Sync mutations, cache invalidation etc between browser tabs.
   * This should help with tabs getting out of sync and improve performance if the user has the site open in multiple tabs.
   */
  broadcastQueryClient({
    queryClient: AssistQueryClient,
    broadcastChannel: `assist-${SITE_ENV}-${APP_VERSION}`
  });
}

/**
 * ASSIST:API client provider
 */
export function AssistQueryClientProvider({ children }: React.PropsWithChildren): ReactElement {
  return <QueryClientProvider client={AssistQueryClient}>{children}</QueryClientProvider>;
}

/**
 * Assist useQuery custom hook
 * It will make the api call only if some conditions are true
 * The call defines filters or
 * the call defines pagination or
 * the call tries to fetch only one entity by defining an 'id' or
 * the call allows to fetch all from the api with the allowNoFilters flag
 */

export const useQueryAssist = <
  TQueryFnData,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TQueryFnData, TQueryKey>,
  options?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey' | 'queryFn'
  > & { allowNoFilters?: boolean }
): UseQueryResult<TData, TError> => {
  // queryKey can be a string or an array. params will be the last element of the array
  const params = Array.isArray(queryKey) ? queryKey[queryKey.length - 1] : {};

  // params can have id, pagination
  // or anything in the filter section that has a value not null not undefined
  const hasFilters =
    !!params?.id ||
    (!!params?.pagination?.page && !!params?.pagination?.itemsPerPage) ||
    (!!params?.filters && Object.entries(params?.filters).findIndex((v) => !!v[1]) > -1);

  return useQuery(
    queryKey,
    queryFn,
    options?.allowNoFilters || hasFilters
      ? options
      : {
          enabled: false,
          ...options
        }
  );
};
